import { useState } from "react";
import axios from "axios";
import { GraduationCap, Check, Save, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SKILL_RE = /^[A-Za-z0-9 \-/À-ÿĂÂÎȘȚăâîșț.+#&]{1,30}$/;

// Commit a typed-but-not-confirmed skill so it isn't lost on save
function withPending(list, pending) {
  const v = (pending || "").trim().replace(/\s+/g, " ");
  if (!v || !SKILL_RE.test(v)) return list;
  if (list.some((x) => x.toLowerCase() === v.toLowerCase())) return list;
  return [...list, v];
}

function SkillPicker({ value, available, onChange, placeholder, testidPrefix, onInputChange }) {
  const [input, setInput] = useState("");
  const add = (s) => {
    const v = s.trim().replace(/\s+/g, " ");
    if (!v || !SKILL_RE.test(v)) return;
    if (value.some((x) => x.toLowerCase() === v.toLowerCase())) { setInput(""); onInputChange?.(""); return; }
    onChange([...value, v]);
    setInput("");
    onInputChange?.("");
  };
  const remove = (s) => onChange(value.filter((x) => x !== s));
  const suggestions = (available || [])
    .filter((s) => !value.some((v) => v.toLowerCase() === s.toLowerCase()))
    .filter((s) => !input || s.toLowerCase().includes(input.toLowerCase()))
    .slice(0, 6);

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.length === 0 && <span className="text-[11px] text-zinc-400 italic">Niciuna selectată.</span>}
        {value.map((s) => (
          <span key={s} data-testid={`${testidPrefix}-pill-${s}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs font-medium rounded-full bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900">
            {s}
            <button onClick={() => remove(s)} className="ml-0.5 inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-white/20 dark:hover:bg-black/10">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="relative">
        <input
          data-testid={`${testidPrefix}-input`}
          value={input}
          onChange={(e) => { setInput(e.target.value); onInputChange?.(e.target.value); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (suggestions[0]) add(suggestions[0]); else add(input); } }}
          placeholder={placeholder}
          className="w-full h-9 px-3 rounded-lg border border-border bg-white dark:bg-zinc-950 text-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-100"
        />
        {input && suggestions.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white dark:bg-zinc-900 border border-border rounded-lg shadow-lg overflow-hidden">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => add(s)}
                data-testid={`${testidPrefix}-suggestion-${s}`}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-50 dark:hover:bg-zinc-800"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function MentorProfileEditor({ employee, allSkills, onSaved, onCancel }) {
  const [active, setActive] = useState(!!employee.mentor_active);
  const [skills, setSkills] = useState(employee.mentor_skills || []);
  const [status, setStatus] = useState(employee.mentor_status || "available");
  const [pending, setPending] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const finalSkills = withPending(skills, pending);
    setSaving(true);
    try {
      const r = await axios.put(`${API}/employees/${employee.id}/mentor-profile`, { active, skills: finalSkills, status });
      toast.success(active ? "Profil mentor actualizat" : "Te-ai retras din pool-ul de mentori");
      onSaved?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="mentor-profile-editor" className="space-y-3">
      <label className="flex items-center gap-2 text-sm">
        <input
          data-testid="mentor-active-toggle"
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="h-4 w-4 rounded border-border accent-zinc-900 dark:accent-zinc-100"
        />
        <span className="font-medium">Sunt disponibil ca mentor</span>
      </label>

      {active && (
        <>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-1.5 block">
              Pe ce competențe oferi mentorat?
            </label>
            <SkillPicker
              value={skills}
              available={(allSkills || []).map((s) => s.name)}
              onChange={setSkills}
              onInputChange={setPending}
              placeholder="Tastează un skill și Enter..."
              testidPrefix="mentor-skill"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-1.5 block">Status curent</label>
            <div className="inline-flex items-center gap-1 p-1 rounded-full border border-border bg-zinc-50 dark:bg-zinc-900">
              {[
                { k: "available", label: "Disponibil", cls: "bg-emerald-500 text-white" },
                { k: "unavailable", label: "Ocupat", cls: "bg-amber-500 text-white" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  data-testid={`mentor-status-${opt.k}`}
                  onClick={() => setStatus(opt.k)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    status === opt.k ? opt.cls : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-zinc-500 dark:text-zinc-400">
              Status-ul se setează automat „Ocupat" când accepți o cerere. Îl poți pune înapoi pe „Disponibil" când finalizezi.
            </p>
          </div>
        </>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          data-testid="mentor-save-btn"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" /> {saving ? "Se salvează..." : "Salvează"}
        </button>
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-border text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800"
        >
          Anulează
        </button>
      </div>
    </div>
  );
}

export function MenteeSkillsEditor({ employee, allSkills, onSaved, onCancel }) {
  const [skills, setSkills] = useState(employee.mentee_skills || []);
  const [pending, setPending] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const finalSkills = withPending(skills, pending);
    setSaving(true);
    try {
      const r = await axios.put(`${API}/employees/${employee.id}/mentee-skills`, { skills: finalSkills });
      toast.success("Interesele de învățare actualizate");
      onSaved?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="mentee-skills-editor" className="space-y-3">
      <div>
        <label className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-1.5 block">
          Ce ai vrea să înveți?
        </label>
        <SkillPicker
          value={skills}
          available={(allSkills || []).map((s) => s.name)}
          onChange={setSkills}
          onInputChange={setPending}
          placeholder="Adaugă o competență de învățat..."
          testidPrefix="mentee-skill"
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          data-testid="mentee-save-btn"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" /> {saving ? "Se salvează..." : "Salvează"}
        </button>
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-border text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800"
        >
          Anulează
        </button>
      </div>
    </div>
  );
}

export function MentorStatusBadge({ employee, size = "sm" }) {
  if (!employee?.mentor_active) return null;
  const available = (employee.mentor_status || "available") === "available";
  const cls = available
    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
    : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
  const sz = size === "sm" ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5";
  return (
    <span data-testid={`mentor-badge-${employee.id}`} className={`inline-flex items-center gap-1 font-medium rounded-full ${cls} ${sz}`}>
      <GraduationCap className={size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3"} />
      {available ? "Mentor" : "Mentor ocupat"}
    </span>
  );
}

export function RequestMentorshipButton({ mentor }) {
  const [skill, setSkill] = useState(mentor.mentor_skills?.[0] || "");
  const [message, setMessage] = useState("");
  const [reqName, setReqName] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  if (mentor.mentor_status !== "available") {
    return (
      <div className="text-[11px] text-amber-700 dark:text-amber-400">
        Mentorul este momentan ocupat. Verifică mai târziu.
      </div>
    );
  }

  const sendEmail = async () => {
    if (!mentor.email) {
      toast.error("Mentorul nu are email setat.");
      return;
    }
    if (!reqEmail.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(reqEmail.trim())) {
      toast.error("Completează un email valid ca să te poată contacta mentorul.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/mentorship/request`, {
        mentor_email: mentor.email,
        mentor_name: mentor.name,
        skill,
        requester_name: reqName.trim(),
        requester_email: reqEmail.trim(),
        message: message.trim(),
      });
      toast.success("Cererea de mentorat a fost trimisă pe email!");
      setOpen(false);
      setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu am putut trimite emailul.");
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        data-testid={`request-mentorship-btn-${mentor.id}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors"
      >
        <GraduationCap className="h-3.5 w-3.5" /> Cere mentorat
      </button>
    );
  }

  return (
    <div data-testid="request-mentorship-form" className="space-y-2 p-3 rounded-lg border border-border bg-zinc-50 dark:bg-zinc-800/40">
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 block mb-1">Competență</label>
        <select
          data-testid="request-skill-select"
          value={skill}
          onChange={(e) => setSkill(e.target.value)}
          className="w-full h-8 px-2 rounded-md border border-border bg-white dark:bg-zinc-950 text-sm focus:outline-none"
        >
          {(mentor.mentor_skills || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 block mb-1">Numele tău</label>
          <input
            data-testid="request-name-input"
            value={reqName}
            onChange={(e) => setReqName(e.target.value)}
            maxLength={80}
            placeholder="Ex: Ana Pop"
            className="w-full h-8 px-2 rounded-md border border-border bg-white dark:bg-zinc-950 text-sm focus:outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 block mb-1">Emailul tău *</label>
          <input
            data-testid="request-email-input"
            type="email"
            value={reqEmail}
            onChange={(e) => setReqEmail(e.target.value)}
            maxLength={120}
            placeholder="ana.pop@ezipper.ro"
            className="w-full h-8 px-2 rounded-md border border-border bg-white dark:bg-zinc-950 text-sm focus:outline-none"
          />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 block mb-1">Mesaj (opțional)</label>
        <textarea
          data-testid="request-message-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="Spune-i ce vrei să înveți..."
          className="w-full px-2 py-1.5 rounded-md border border-border bg-white dark:bg-zinc-950 text-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-900"
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          data-testid="request-submit-btn"
          onClick={sendEmail}
          disabled={!skill || sending}
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
        >
          <Check className="h-3.5 w-3.5" /> {sending ? "Se trimite..." : "Trimite cererea"}
        </button>
        <button onClick={() => setOpen(false)} className="inline-flex items-center px-3 h-8 rounded-md border border-border text-xs">
          Anulează
        </button>
      </div>
    </div>
  );
}
